import type {
  InteractiveZone,
  InteractiveZonesInfo
} from "@gettactic/helpers/src/map/types";
import type { MapCanvasValue } from "../components/map/contexts/MapCanvasContext/MapCanvasContext.types";
import {
  DEFAULT_NODE_CIRCLE_RADIUS,
  DEFAULT_NODE_CIRCLE_STROKE_WIDTH
} from "../components/map/defines";
import { getOrientationLayout } from "../components/map/shapes/SpotShape/helpers";
import type { SpotShape } from "../types";
import { getEffectiveSpotAppearance } from "./spots";
import { computeStateIconCenterOffset } from "./stateIconCentering";

export function buildInteractiveZonesFromMapData(
  mapData: MapCanvasValue
): InteractiveZonesInfo {
  const { editingZoneWidth, editingZoneHeight, shapes } = mapData;

  const w = editingZoneWidth;
  const h = editingZoneHeight;

  const mapInfo = {
    attrs: {
      width: String(w),
      height: String(h),
      viewBox: `0 0 ${w} ${h}`
    },
    width: w,
    height: h,
    vbWidth: w,
    vbHeight: h,
    startX: 0,
    startY: 0,
    defs: ""
  };

  const centerX = w / 2;
  const centerY = h / 10;

  const zones: InteractiveZone[] = [];

  for (const shape of shapes) {
    if (shape.type !== "spot") continue;
    const spot = shape as SpotShape;
    if (!spot.resource_id) continue;

    // Use the same layout logic as SpotShapeComponent
    const layout = getOrientationLayout(
      getEffectiveSpotAppearance(spot.spotType, spot.spotAppearance),
      spot.orientation,
      spot.labelPlacement,
      spot.labelOrientation || "auto",
      0,
      0,
      spot.spacing || 0,
      spot.rowSpacing || 0,
      spot.width,
      spot.height
    );

    const r = spot.circleProps?.radius || DEFAULT_NODE_CIRCLE_RADIUS;
    const strokeWidth =
      spot.circleProps?.strokeWidth || DEFAULT_NODE_CIRCLE_STROKE_WIDTH;

    // Matches InteractiveZoneSVG/InteractiveZone: a spot type with no rect
    // of its own (Node) has nothing that naturally scales/centers with a
    // configured highlight area (e.g. a grid tool cell) — center the dot on
    // it instead of rendering at Node's fixed default anchor.
    const hasOwnRect = !!(layout.rect.width && layout.rect.height);
    const dotCenterOffset = !hasOwnRect
      ? computeStateIconCenterOffset(spot)
      : null;
    const cx = layout.circle.x + (dotCenterOffset?.offsetX ?? 0);
    const cy = layout.circle.y + (dotCenterOffset?.offsetY ?? 0);

    const containerTransform = `translate(${spot.x}, ${spot.y})${spot.rotation ? ` rotate(${spot.rotation})` : ""}`;

    const html = `<circle cx="${cx}" cy="${cy}" r="${r}" stroke-width="${strokeWidth}" />`;

    // Zone position in map coordinates
    const zoneX = spot.x + cx;
    const zoneY = spot.y + cy;

    // Centering coordinates (same formula as parseInteractiveZonesSvg)
    const destX =
      ((zoneX < centerX
        ? Math.abs(centerX - zoneX)
        : -1 * Math.abs(zoneX - centerX)) *
        w) /
      w;
    const destY =
      ((zoneY < centerY
        ? Math.abs(centerY - zoneY)
        : -1 * Math.abs(zoneY - centerY)) *
        h) /
      h;

    zones.push({
      id: spot.resource_id,
      x: spot.x,
      y: spot.y,
      width: r * 2 + strokeWidth,
      height: r * 2 + strokeWidth,
      html,
      destX,
      destY,
      rotation: spot.rotation || 0,
      containerTransformation: containerTransform,
      mapBuilderContainerTransformation: containerTransform,
      circle: {
        cx,
        cy,
        r,
        strokeWidth
      }
    });
  }

  return { mapInfo, zones };
}
